import type {
  CharterFeederPriceCreateItem,
  CharterFeederPriceUpdateItem,
  CharterFeederPricesBulkPayload,
} from '~/services/FlightsService';
import { formatLocalDateISOUTC, getDayJs } from '~/services/TimeService';

import {
  DEFAULT_CHARTER_FEEDER_DEPARTURE_INTERVAL_DAYS,
  DEFAULT_CHARTER_FEEDER_RETURN_OFFSET_DAYS,
  DEFAULT_CHARTER_FEEDER_START_DATE,
  DEFAULT_CHARTER_FEEDER_WILDCARD_AIRPORT_CODE,
  DEFAULT_FEEDER_ORIGIN_CODES,
  FEEDER_CODE_PATTERN,
  ISO_DATE_PATTERN,
  MEL_FEEDER_ORIGIN_CODE,
  ROUTE_AIRPORT_CODE_PATTERN,
} from './CharterFeederPricesConstants';
import {
  type BuildCharterFeederBulkPayloadResult,
  CHARTER_FEEDER_BASE_SUFFIX,
  CHARTER_FEEDER_BUFFER_SUFFIX,
  CHARTER_FEEDER_DATE_FIELD,
  CHARTER_FEEDER_DESTINATION_FIELD,
  CHARTER_FEEDER_ORIGIN_FIELD,
  type CharterFeederColumn,
  type CharterFeederPersistedRow,
  type CharterFeederSheetRow,
  type NormalizedCharterFeederSheet,
} from './CharterFeederPricesTypes';

type ParsedNumberCell = { kind: 'empty' } | { kind: 'invalid' } | { kind: 'number'; value: number };

type DraftCell = Omit<CharterFeederPriceCreateItem, 'ModifiedBy'>;
type DraftCellEntry = {
  key: string;
  rowId: string;
  feederCode: string;
  cell: DraftCell;
};

const defaultFeederOrder = new Map<string, number>(
  DEFAULT_FEEDER_ORIGIN_CODES.map((code, index) => [code, index] as [string, number]),
);

function trimUpper(value: unknown): string {
  if (typeof value !== 'string') {
    return '';
  }

  return value.trim().toUpperCase();
}

function toIsoDate(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }

  const str = String(value).trim();

  // Bare 'YYYY-MM-DD' strings are calendar dates — formatLocalDateISOUTC would shift them by the browser's UTC offset.
  if (ISO_DATE_PATTERN.test(str)) {
    return str;
  }

  const formatted = formatLocalDateISOUTC(value instanceof Date ? value : str);
  return formatted === 'Invalid Date' ? '' : formatted;
}

function parseNumberCell(value: unknown): ParsedNumberCell {
  if (value === '' || value === null || value === undefined) {
    return { kind: 'empty' };
  }

  const parsed = typeof value === 'number' ? value : Number(String(value).trim());
  if (!Number.isFinite(parsed)) {
    return { kind: 'invalid' };
  }

  return { kind: 'number', value: parsed };
}

function parseCreatedAtMs(value: unknown): number {
  if (typeof value !== 'string') {
    return 0;
  }

  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function isMelFeederCode(value: string): boolean {
  return trimUpper(value) === MEL_FEEDER_ORIGIN_CODE;
}

function compareFeederColumns(
  [leftCode, leftCreatedAt]: [string, number],
  [rightCode, rightCreatedAt]: [string, number],
): number {
  const leftDefaultIndex = defaultFeederOrder.get(leftCode);
  const rightDefaultIndex = defaultFeederOrder.get(rightCode);

  if (leftDefaultIndex !== undefined || rightDefaultIndex !== undefined) {
    return (leftDefaultIndex ?? Number.MAX_SAFE_INTEGER) - (rightDefaultIndex ?? Number.MAX_SAFE_INTEGER);
  }

  if (leftCreatedAt !== rightCreatedAt) {
    return leftCreatedAt - rightCreatedAt;
  }

  return leftCode.localeCompare(rightCode);
}

function compareRows(a: CharterFeederSheetRow, b: CharterFeederSheetRow): number {
  const byDate = String(a[CHARTER_FEEDER_DATE_FIELD]).localeCompare(String(b[CHARTER_FEEDER_DATE_FIELD]));
  if (byDate !== 0) {
    return byDate;
  }

  const byOrigin = String(a[CHARTER_FEEDER_ORIGIN_FIELD]).localeCompare(String(b[CHARTER_FEEDER_ORIGIN_FIELD]));
  if (byOrigin !== 0) {
    return byOrigin;
  }

  return String(a[CHARTER_FEEDER_DESTINATION_FIELD]).localeCompare(String(b[CHARTER_FEEDER_DESTINATION_FIELD]));
}

function withAllFeederFields(row: CharterFeederSheetRow, columns: Array<CharterFeederColumn>): CharterFeederSheetRow {
  const next = { ...row };

  for (const column of columns) {
    const baseField = toFeederBaseField(column.code);
    const bufferField = toFeederBufferField(column.code);
    next[baseField] = row[baseField] ?? null;
    next[bufferField] = isMelFeederCode(column.code) ? 0 : (row[bufferField] ?? null);
  }

  return next;
}

function createCellKey({
  date,
  originAirportCode,
  destinationAirportCode,
  feederOriginAirportCode,
}: {
  date: string;
  originAirportCode: string;
  destinationAirportCode: string;
  feederOriginAirportCode: string;
}): string {
  return `${date}|${originAirportCode}|${destinationAirportCode}|${feederOriginAirportCode}`;
}

function createRowLabel(row: CharterFeederSheetRow, rowIndex: number): string {
  const date = String(row[CHARTER_FEEDER_DATE_FIELD] ?? '').trim();
  const origin = String(row[CHARTER_FEEDER_ORIGIN_FIELD] ?? '').trim();
  const destination = String(row[CHARTER_FEEDER_DESTINATION_FIELD] ?? '').trim();
  const route = [date || 'n/a', origin || 'n/a', destination || 'n/a'].join(' / ');
  return `Row ${rowIndex + 1} (${route})`;
}

function createDefaultCharterFeederRouteRows(columns: Array<CharterFeederColumn>): Array<CharterFeederSheetRow> {
  const rows: Array<CharterFeederSheetRow> = [];
  const capDate = getDayJs().add(1, 'year').startOf('day');
  let departureDate = getDayJs(DEFAULT_CHARTER_FEEDER_START_DATE);
  let returnDate = departureDate.add(DEFAULT_CHARTER_FEEDER_RETURN_OFFSET_DAYS, 'day');

  while (!returnDate.isAfter(capDate, 'day')) {
    const departure = {
      date: departureDate.format('YYYY-MM-DD'),
      originAirportCode: DEFAULT_CHARTER_FEEDER_WILDCARD_AIRPORT_CODE,
      destinationAirportCode: MEL_FEEDER_ORIGIN_CODE,
    };
    const returning = {
      date: returnDate.format('YYYY-MM-DD'),
      originAirportCode: MEL_FEEDER_ORIGIN_CODE,
      destinationAirportCode: DEFAULT_CHARTER_FEEDER_WILDCARD_AIRPORT_CODE,
    };

    rows.push(createEmptyCharterFeederRow(toSheetRowKey(departure), columns, departure));
    rows.push(createEmptyCharterFeederRow(toSheetRowKey(returning), columns, returning));
    departureDate = departureDate.add(DEFAULT_CHARTER_FEEDER_DEPARTURE_INTERVAL_DAYS, 'day');
    returnDate = departureDate.add(DEFAULT_CHARTER_FEEDER_RETURN_OFFSET_DAYS, 'day');
  }

  return rows;
}

function createDefaultCharterFeederRouteKeySet(): Set<string> {
  return new Set(createDefaultCharterFeederRouteRows([]).map((row) => row.id));
}

function formatDirectionAirportCode(value: unknown): string {
  const airportCode = trimUpper(value);
  return airportCode === 'ALL' ? 'X' : airportCode;
}

function normalizeOriginalRows(rows: Array<CharterFeederPersistedRow>): Map<string, CharterFeederPersistedRow> {
  const byKey = new Map<string, CharterFeederPersistedRow>();

  for (const row of rows) {
    const key = createCellKey({
      date: toIsoDate(row.Date),
      originAirportCode: trimUpper(row.OriginAirportCode),
      destinationAirportCode: trimUpper(row.DestinationAirportCode),
      feederOriginAirportCode: trimUpper(row.FeederOriginAirportCode),
    });
    byKey.set(key, row);
  }

  return byKey;
}

function buildUpdateChanges({
  originalRow,
  draftCell,
}: {
  originalRow: CharterFeederPersistedRow;
  draftCell: DraftCell;
}): CharterFeederPriceUpdateItem['changes'] {
  const changes: CharterFeederPriceUpdateItem['changes'] = {};

  if (toIsoDate(originalRow.Date) !== draftCell.Date) {
    changes.Date = draftCell.Date;
  }

  if (trimUpper(originalRow.OriginAirportCode) !== draftCell.OriginAirportCode) {
    changes.OriginAirportCode = draftCell.OriginAirportCode;
  }

  if (trimUpper(originalRow.DestinationAirportCode) !== draftCell.DestinationAirportCode) {
    changes.DestinationAirportCode = draftCell.DestinationAirportCode;
  }

  if (trimUpper(originalRow.FeederOriginAirportCode) !== draftCell.FeederOriginAirportCode) {
    changes.FeederOriginAirportCode = draftCell.FeederOriginAirportCode;
  }

  if (Number(originalRow.BasePrice) !== draftCell.BasePrice) {
    changes.BasePrice = draftCell.BasePrice;
  }

  if (Number(originalRow.BufferPrice) !== draftCell.BufferPrice) {
    changes.BufferPrice = draftCell.BufferPrice;
  }

  return changes;
}

export function toFeederBaseField(feederCode: string): string {
  return `${trimUpper(feederCode)}${CHARTER_FEEDER_BASE_SUFFIX}`;
}

export function toFeederBufferField(feederCode: string): string {
  return `${trimUpper(feederCode)}${CHARTER_FEEDER_BUFFER_SUFFIX}`;
}

export function toSheetRowKey({
  date,
  originAirportCode,
  destinationAirportCode,
}: {
  date: string;
  originAirportCode: string;
  destinationAirportCode: string;
}): string {
  return `${date}|${originAirportCode}|${destinationAirportCode}`;
}

export function normalizeFeederCode(value: string): string {
  return trimUpper(value);
}

export function isMelFeederOriginCode(value: string): boolean {
  return isMelFeederCode(value);
}

export function formatCharterFeederDirection({
  originAirportCode,
  destinationAirportCode,
}: Pick<CharterFeederSheetRow, 'originAirportCode' | 'destinationAirportCode'>): string {
  return `${formatDirectionAirportCode(originAirportCode)} ➞ ${formatDirectionAirportCode(destinationAirportCode)}`;
}

export function isSameRouteRow(
  left: Pick<CharterFeederSheetRow, 'date' | 'originAirportCode' | 'destinationAirportCode'>,
  right: Pick<CharterFeederSheetRow, 'date' | 'originAirportCode' | 'destinationAirportCode'>,
): boolean {
  return (
    toIsoDate(left.date) === toIsoDate(right.date) &&
    trimUpper(left.originAirportCode) === trimUpper(right.originAirportCode) &&
    trimUpper(left.destinationAirportCode) === trimUpper(right.destinationAirportCode)
  );
}

export function createEmptyCharterFeederRow(
  rowId: string,
  columns: Array<CharterFeederColumn>,
  initial?: Partial<Pick<CharterFeederSheetRow, 'date' | 'originAirportCode' | 'destinationAirportCode'>>,
): CharterFeederSheetRow {
  const row: CharterFeederSheetRow = {
    id: rowId,
    [CHARTER_FEEDER_DATE_FIELD]: initial?.date ?? '',
    [CHARTER_FEEDER_ORIGIN_FIELD]: initial?.originAirportCode ?? '',
    [CHARTER_FEEDER_DESTINATION_FIELD]: initial?.destinationAirportCode ?? '',
  };

  return withAllFeederFields(row, columns);
}

export function addFeederColumnToRows(
  rows: Array<CharterFeederSheetRow>,
  feederCode: string,
): Array<CharterFeederSheetRow> {
  const baseField = toFeederBaseField(feederCode);
  const bufferField = toFeederBufferField(feederCode);

  return rows.map((row) => ({
    ...row,
    [baseField]: row[baseField] ?? null,
    [bufferField]: isMelFeederCode(feederCode) ? 0 : (row[bufferField] ?? null),
  }));
}

export function removeFeederColumnFromRows(
  rows: Array<CharterFeederSheetRow>,
  feederCode: string,
): Array<CharterFeederSheetRow> {
  const baseField = toFeederBaseField(feederCode);
  const bufferField = toFeederBufferField(feederCode);

  return rows.map((row) => {
    const next = { ...row };
    delete next[baseField];
    delete next[bufferField];
    return next;
  });
}

export function clearCharterFeederRowValues(
  row: CharterFeederSheetRow,
  columns: Array<CharterFeederColumn>,
): CharterFeederSheetRow {
  const next = { ...row };

  for (const column of columns) {
    next[toFeederBaseField(column.code)] = null;
    next[toFeederBufferField(column.code)] = isMelFeederCode(column.code) ? 0 : null;
  }

  return next;
}

export function sortCharterFeederRows(rows: Array<CharterFeederSheetRow>): Array<CharterFeederSheetRow> {
  return [...rows].sort(compareRows);
}

export function cloneCharterFeederRows(rows: Array<CharterFeederSheetRow>): Array<CharterFeederSheetRow> {
  return rows.map((row) => ({ ...row }));
}

export function cloneCharterFeederColumns(columns: Array<CharterFeederColumn>): Array<CharterFeederColumn> {
  return columns.map((column) => ({ ...column }));
}

export function normalizeCharterFeederSheet(rows: Array<CharterFeederPersistedRow>): NormalizedCharterFeederSheet {
  const latestCreatedAtByFeeder = new Map<string, number>(
    DEFAULT_FEEDER_ORIGIN_CODES.map((code) => [code, 0] as [string, number]),
  );
  const rowsByKey = new Map<string, CharterFeederSheetRow>();

  for (const row of rows) {
    const feederCode = trimUpper(row.FeederOriginAirportCode);
    const date = toIsoDate(row.Date);
    const originAirportCode = trimUpper(row.OriginAirportCode);
    const destinationAirportCode = trimUpper(row.DestinationAirportCode);

    if (!feederCode || !date || !originAirportCode || !destinationAirportCode) {
      continue;
    }

    const rowKey = toSheetRowKey({ date, originAirportCode, destinationAirportCode });
    const baseField = toFeederBaseField(feederCode);
    const bufferField = toFeederBufferField(feederCode);

    const existingRow = rowsByKey.get(rowKey) ?? {
      id: rowKey,
      [CHARTER_FEEDER_DATE_FIELD]: date,
      [CHARTER_FEEDER_ORIGIN_FIELD]: originAirportCode,
      [CHARTER_FEEDER_DESTINATION_FIELD]: destinationAirportCode,
    };

    existingRow[baseField] = Number(row.BasePrice);
    existingRow[bufferField] = isMelFeederCode(feederCode) ? 0 : Number(row.BufferPrice);
    rowsByKey.set(rowKey, existingRow);

    const createdAtMs = parseCreatedAtMs(row.CreatedDate);
    latestCreatedAtByFeeder.set(feederCode, Math.max(latestCreatedAtByFeeder.get(feederCode) ?? 0, createdAtMs));
  }

  const columns = Array.from(latestCreatedAtByFeeder.entries())
    .sort(compareFeederColumns)
    .map(([code]) => ({ code, isNew: false }));

  // Suppress generated placeholders for dates with a persisted row (prevents ghost rows for the opposite direction).
  const persistedDates = new Set<string>(
    Array.from(rowsByKey.values()).map((r) => String(r[CHARTER_FEEDER_DATE_FIELD])),
  );

  for (const row of createDefaultCharterFeederRouteRows(columns)) {
    if (!rowsByKey.has(row.id) && !persistedDates.has(String(row[CHARTER_FEEDER_DATE_FIELD]))) {
      rowsByKey.set(row.id, row);
    }
  }

  const sortedRows = sortCharterFeederRows(
    Array.from(rowsByKey.values()).map((row) => withAllFeederFields(row, columns)),
  );

  return {
    rows: sortedRows,
    columns,
  };
}

export function hasCharterFeederBulkChanges(payload: CharterFeederPricesBulkPayload): boolean {
  return Boolean(payload.create?.length || payload.update?.length || payload.deleteIds?.length);
}

export function buildCharterFeederBulkPayload({
  rows,
  columns,
  originalRows,
}: {
  rows: Array<CharterFeederSheetRow>;
  columns: Array<CharterFeederColumn>;
  originalRows: Array<CharterFeederPersistedRow>;
}): BuildCharterFeederBulkPayloadResult {
  const validationErrors: Array<string> = [];
  const normalizedColumns = columns.map((column) => ({
    code: trimUpper(column.code),
    isNew: column.isNew,
  }));

  const duplicateColumns = normalizedColumns.filter(
    (column, index) => normalizedColumns.findIndex((entry) => entry.code === column.code) !== index,
  );
  if (duplicateColumns.length > 0) {
    validationErrors.push(
      `Duplicate feeder columns found: ${Array.from(new Set(duplicateColumns.map((c) => c.code))).join(', ')}`,
    );
  }

  const invalidColumns = normalizedColumns.filter((column) => !FEEDER_CODE_PATTERN.test(column.code));
  if (invalidColumns.length > 0) {
    validationErrors.push(`Invalid feeder codes: ${invalidColumns.map((c) => c.code).join(', ')}`);
  }

  const originalByKey = normalizeOriginalRows(originalRows);
  const persistedRowKeys = new Set(
    originalRows.map((row) =>
      toSheetRowKey({
        date: toIsoDate(row.Date),
        originAirportCode: trimUpper(row.OriginAirportCode),
        destinationAirportCode: trimUpper(row.DestinationAirportCode),
      }),
    ),
  );
  const generatedDefaultRowKeys = createDefaultCharterFeederRouteKeySet();
  const draftByKey = new Map<string, DraftCellEntry>();
  const draftEntries: Array<DraftCellEntry> = [];
  const filledCellsByNewColumn = new Map(
    normalizedColumns.filter((column) => column.isNew).map((column) => [column.code, 0]),
  );

  rows.forEach((row, rowIndex) => {
    const rowLabel = createRowLabel(row, rowIndex);
    const filledCells: Array<{ feederCode: string; basePrice: number; bufferPrice: number }> = [];

    for (const column of normalizedColumns) {
      const feederCode = column.code;
      const baseField = toFeederBaseField(feederCode);
      const bufferField = toFeederBufferField(feederCode);
      const isMelFeeder = isMelFeederCode(feederCode);

      const baseCell = parseNumberCell(row[baseField]);

      if (baseCell.kind === 'invalid') {
        validationErrors.push(`${rowLabel}: Base must be a numeric value for feeder ${feederCode}.`);
        continue;
      }

      if (isMelFeeder) {
        if (baseCell.kind === 'empty') {
          continue;
        }

        filledCells.push({
          feederCode,
          basePrice: baseCell.value,
          bufferPrice: 0,
        });
        continue;
      }

      const bufferCell = parseNumberCell(row[bufferField]);

      if (bufferCell.kind === 'invalid') {
        validationErrors.push(`${rowLabel}: Base and Buffer must be numeric values for feeder ${feederCode}.`);
        continue;
      }

      if (baseCell.kind === 'empty' && bufferCell.kind === 'empty') {
        continue;
      }

      if (baseCell.kind !== 'number' || bufferCell.kind !== 'number') {
        validationErrors.push(
          `${rowLabel}: Base and Buffer must both be filled or both be blank for feeder ${feederCode}.`,
        );
        continue;
      }

      filledCells.push({
        feederCode,
        basePrice: baseCell.value,
        bufferPrice: bufferCell.value,
      });
    }

    if (filledCells.length === 0) {
      // An empty row that maps to an existing persisted route is treated as a cascade delete.
      // Generated default rows stay in the draft as placeholders until a user fills a feeder value.
      if (!persistedRowKeys.has(String(row.id)) && !generatedDefaultRowKeys.has(String(row.id))) {
        validationErrors.push(`${rowLabel}: add at least one Base/Buffer pair before saving, or remove the row.`);
      }
      return;
    }

    const date = toIsoDate(row[CHARTER_FEEDER_DATE_FIELD]);
    if (!date) {
      validationErrors.push(`${rowLabel}: Date is required when a feeder price is filled.`);
      return;
    }

    const originAirportCode = trimUpper(row[CHARTER_FEEDER_ORIGIN_FIELD]);
    if (!ROUTE_AIRPORT_CODE_PATTERN.test(originAirportCode)) {
      validationErrors.push(`${rowLabel}: Origin Airport must have exactly 3 non-whitespace characters.`);
      return;
    }

    const destinationAirportCode = trimUpper(row[CHARTER_FEEDER_DESTINATION_FIELD]);
    if (!ROUTE_AIRPORT_CODE_PATTERN.test(destinationAirportCode)) {
      validationErrors.push(`${rowLabel}: Destination Airport must have exactly 3 non-whitespace characters.`);
      return;
    }

    for (const cell of filledCells) {
      const key = createCellKey({
        date,
        originAirportCode,
        destinationAirportCode,
        feederOriginAirportCode: cell.feederCode,
      });

      if (draftByKey.has(key)) {
        validationErrors.push(`${rowLabel}: Duplicate row key found for feeder ${cell.feederCode}.`);
        continue;
      }

      const entry: DraftCellEntry = {
        key,
        rowId: String(row.id),
        feederCode: cell.feederCode,
        cell: {
          Date: date,
          OriginAirportCode: originAirportCode,
          DestinationAirportCode: destinationAirportCode,
          FeederOriginAirportCode: cell.feederCode,
          BasePrice: cell.basePrice,
          BufferPrice: cell.bufferPrice,
        },
      };
      draftByKey.set(key, entry);
      draftEntries.push(entry);

      if (filledCellsByNewColumn.has(cell.feederCode)) {
        filledCellsByNewColumn.set(cell.feederCode, (filledCellsByNewColumn.get(cell.feederCode) ?? 0) + 1);
      }
    }
  });

  for (const [columnCode, filledCellsCount] of filledCellsByNewColumn.entries()) {
    if (filledCellsCount === 0) {
      validationErrors.push(`Feeder column ${columnCode} must include at least one fully filled Base/Buffer pair.`);
    }
  }

  if (validationErrors.length > 0) {
    return {
      payload: {},
      hasChanges: false,
      validationErrors: Array.from(new Set(validationErrors)),
    };
  }

  const create: Array<CharterFeederPriceCreateItem> = [];
  const update: Array<CharterFeederPriceUpdateItem> = [];
  const deleteIds: Array<number> = [];
  const matchedOriginalIds = new Set<number>();

  for (const draftEntry of draftEntries) {
    const exactOriginalMatch = originalByKey.get(draftEntry.key);
    const fallbackOriginalKey = `${draftEntry.rowId}|${draftEntry.feederCode}`;
    const fallbackOriginalMatch = originalByKey.get(fallbackOriginalKey);

    const matchedOriginalRow =
      exactOriginalMatch && !matchedOriginalIds.has(Number(exactOriginalMatch.Id))
        ? exactOriginalMatch
        : fallbackOriginalMatch && !matchedOriginalIds.has(Number(fallbackOriginalMatch.Id))
          ? fallbackOriginalMatch
          : undefined;

    if (!matchedOriginalRow) {
      create.push(draftEntry.cell);
      continue;
    }

    const matchedId = Number(matchedOriginalRow.Id);
    matchedOriginalIds.add(matchedId);

    const changes = buildUpdateChanges({
      originalRow: matchedOriginalRow,
      draftCell: draftEntry.cell,
    });
    if (Object.keys(changes).length > 0) {
      update.push({
        Id: matchedId,
        changes,
      });
    }
  }

  for (const originalRow of originalRows) {
    const originalId = Number(originalRow.Id);
    if (!matchedOriginalIds.has(originalId)) {
      deleteIds.push(originalId);
    }
  }

  const payload: CharterFeederPricesBulkPayload = {};
  if (create.length > 0) {
    payload.create = create;
  }
  if (update.length > 0) {
    payload.update = update;
  }
  if (deleteIds.length > 0) {
    payload.deleteIds = deleteIds;
  }

  return {
    payload,
    hasChanges: hasCharterFeederBulkChanges(payload),
    validationErrors: [],
  };
}
